/**
 * SS-3944: insurance eligibility verification — the calls to SS-3939's API and the
 * display rules the Insurance card, the result drawer, settings, the guided setup and
 * the worklist share. Everything here but `ivApi` is pure, so it is unit tested alone.
 *
 * The API answers every call with `{ type: 'ok', data }` or `{ type: 'error', code, msg }`
 * (cqAPI resolves both; it does not throw on an HTTP error status).
 */
import moment from "moment";
import { cqAPI } from "../../lib/Auth";

export const MAX_QUESTIONS = 20;
export const MAX_QUESTION_CHARS = 300;
export const DEFAULT_CREDITS = { eligibility: 2, breakdown: 8 };
export const DEFAULT_STALE_DAYS = 30;
export const SETTINGS_PATH = "/settings/insurance-verification";
export const SETUP_PATH = "/settings/insurance-verification/setup";
export const WORKLIST_PATH = "/insurance-verification/worklist";

// ── API ──────────────────────────────────────────────────────────────────────

const enc = encodeURIComponent;
// a check spends credits: it is sent once, never replayed by the client's retry loop
const ONCE = { attempts: 1 };

export const ivApi = {
  getSettings: () => cqAPI.get("/insurance-verification/settings"),
  putSettings: (body) => cqAPI.put("/insurance-verification/settings", body, undefined, ONCE),
  searchPayers: (search) => cqAPI.get(`/insurance-verification/payers?search=${enc(search || "")}`),
  setupPayers: () => cqAPI.get("/insurance-verification/setup/payers"),
  putPayerMap: (entries) => cqAPI.put("/insurance-verification/payer-map", { entries }, undefined, ONCE),
  getPortals: () => cqAPI.get("/insurance-verification/portals"),
  createRegistration: (portalId) => cqAPI.post(`/insurance-verification/portals/${enc(portalId)}/registration`, {}, undefined, ONCE),
  putCredentials: (portalId, body) => cqAPI.put(`/insurance-verification/portals/${enc(portalId)}/credentials`, body, undefined, ONCE),
  validatePortal: (portalId) => cqAPI.post(`/insurance-verification/portals/${enc(portalId)}/validate`, {}, undefined, ONCE),
  getPrefill: (patientId) => cqAPI.get(`/patients/${enc(patientId)}/insurance-verification/prefill`),
  createVerification: (patientId, body) => cqAPI.post(`/patients/${enc(patientId)}/insurance-verifications`, body, undefined, ONCE),
  listVerifications: (patientId) => cqAPI.get(`/patients/${enc(patientId)}/insurance-verifications`),
  getVerification: (id, { raw = false } = {}) => cqAPI.get(`/insurance-verifications/${enc(id)}${raw ? "?raw=1" : ""}`),
  pdfUrl: (id, variant) => `/insurance-verifications/${enc(id)}/pdf?variant=${variant === "answers" ? "answers" : "full"}`,
  downloadPdf: (id, variant) => cqAPI.downloadFile(ivApi.pdfUrl(id, variant), { timeoutMs: 60000 }),
  retryPmsWrite: (id) => cqAPI.post(`/insurance-verifications/${enc(id)}/pms-write`, {}, undefined, ONCE),
  reanswer: (id) => cqAPI.post(`/insurance-verifications/${enc(id)}/answers`, {}, undefined, ONCE),
  getWorklist: (from, to) => cqAPI.get(`/insurance-verification/worklist?from=${enc(from)}&to=${enc(to)}`),
};

export const isOk = (r) => !!r && r.type === "ok";

// the API's "You do not have the right permission." (ERROR_CODES.PERMISSION): the user lacks BUSINESS_SETTINGS
export const PERMISSION_REFUSED = 101;
export const isPermissionRefused = (r) => !!r && r.type !== "ok" && Number(r.code) === PERMISSION_REFUSED;

// an insurer name from the PMS or the network, compared as data: case, accents, spacing and punctuation aside
const nameKey = (s) => String(s || "").normalize("NFKD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/[^a-z0-9]/g, "");

/** The name of the network entry one of these PMS carrier names is (in their order), or "" when none is. */
export const entryNameForCarriers = (entries, carrierNames) => {
  const list = (entries || []).filter((p) => p && p.name);
  for (const carrier of carrierNames || []) {
    const key = nameKey(carrier);
    const entry = key && list.find((p) => nameKey(p.name) === key);
    if (entry) return entry.name;
  }
  return "";
};

/**
 * A payer id's name for these PMS carrier names, for an API whose prefill does not name the patient's
 * insurer (payer_name, SS-4455). Entries of one id can carry different names
 * (CX007 is both "Minnesota Blue Cross Dental (UCCI)" and "United Concordia Companies, Inc."), so the
 * first one says nothing about this practice's insurer: the entry one of the carrier names is; else, the
 * id standing for several names, the first carrier name itself (never another entry's name); else
 * `fallback` (the network's name).
 */
export const payerNameForCarriers = (entries, carrierNames, fallback = "") => {
  const list = (entries || []).filter((p) => p && p.name);
  const matched = entryNameForCarriers(list, carrierNames);
  if (matched) return matched;
  const carrier = (carrierNames || []).map((n) => String(n || "").trim()).find(Boolean);
  const shared = new Set(list.map((p) => nameKey(p.name)).filter(Boolean)).size > 1;
  return shared && carrier ? carrier : fallback;
};

/**
 * Several network entries can share one commonPayerId (GET /payers lists them apart): one
 * payer, as SS-3939 merges them, with the access and portals of all of them, named after the
 * chart's carrier (payerNameForCarriers, SS-4455).
 */
export const mergePayerEntries = (entries, { carrierName = "" } = {}) => {
  const list = (entries || []).filter(Boolean);
  if (!list.length) return null;
  const direct = list.some((p) => p.access === "direct" || p.access === "direct_and_portal");
  const portal = list.some((p) => p.access === "portal_only" || p.access === "direct_and_portal");
  const name = payerNameForCarriers(list, [carrierName], list[0].name);
  return {
    ...list[0],
    name,
    access: direct && portal ? "direct_and_portal" : direct ? "direct" : portal ? "portal_only" : "none",
    portals: [...new Set(list.flatMap((p) => p.portals || []).map(String))],
  };
};

/** Save a Blob URL as a file (cqAPI.downloadFile returns the URL, or nothing on failure). */
export const saveBlobUrl = (url, fileName) => {
  if (typeof url !== "string" || !url) return false;
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => window.URL.revokeObjectURL?.(url), 10000);
  return true;
};

// ── formatting ───────────────────────────────────────────────────────────────

export const formatDate = (d) => (d ? moment(d).format("MMM D, YYYY") : "");
// a benefit period date is a calendar day: read it in UTC so it never shifts a day
export const formatDay = (d) => (d ? moment.utc(d).format("MMM D, YYYY") : "");

export const money = (n) => {
  if (n === undefined || n === null || n === "") return "";
  const x = Number(n);
  if (!Number.isFinite(x)) return String(n);
  return `$${x.toLocaleString("en-US", { minimumFractionDigits: x % 1 ? 2 : 0, maximumFractionDigits: 2 })}`;
};

/** "PreventiveExams" / "MajorRestorative" → "Preventive Exams" / "Major Restorative". */
export const humanize = (s) => String(s || "").replace(/([a-z])([A-Z])/g, "$1 $2").replace(/_/g, " ").trim();

export const networkLabel = (n) => {
  if (!n) return "";
  const status = n.status === "OutOfNetwork" ? "Out-of-network" : n.status === "InNetwork" ? "In-network" : humanize(n.status);
  return n.name && n.name !== n.status ? `${status} (${n.name})` : status;
};

export const percentLabel = (min, max) => {
  if (min === undefined || min === null) return "";
  return min === max || max === undefined || max === null ? `${min}%` : `${min}–${max}%`;
};

// ── the card ─────────────────────────────────────────────────────────────────

const doneResults = (verifications) => (verifications || []).filter((v) => v && v.status === "done");
const byNewest = (a, b) => new Date(b.completed || b.created) - new Date(a.completed || a.created);

/** The patient's latest finished check (done or failed), newest first. */
export const latestFinished = (verifications) => (verifications || [])
  .filter((v) => v && (v.status === "done" || v.status === "failed")).sort(byNewest)[0] || null;

/** A breakdown or check still running (requested, in progress or retrying). */
export const pendingVerification = (verifications) => (verifications || [])
  .filter((v) => v && ["requested", "in_progress", "retrying"].includes(v.status)).sort(byNewest)[0] || null;

const isInactiveError = (v) => v?.status === "failed" && /inactive/i.test(v?.error?.type || "");

/**
 * The card's status line: Verified Active · date, Inactive · date, Not verified, or
 * Stale (an Active result older than the practice's stale days, 30 by default).
 * @returns {{ kind: 'active'|'inactive'|'stale'|'unverified', label: string, date: string }}
 */
export const statusLine = (verifications, { staleDays = DEFAULT_STALE_DAYS, now = new Date() } = {}) => {
  // the latest ANSWER about coverage (a result, or the insurer saying it is inactive): a check
  // the insurer did not answer (unavailable, a portal login problem) leaves that answer standing
  const latest = (verifications || []).filter((v) => v && (v.status === "done" || isInactiveError(v))).sort(byNewest)[0];
  if (!latest) return { kind: "unverified", label: "Not verified", date: "" };
  const when = latest.completed || latest.created;
  const date = formatDate(when);
  const coverage = latest.status === "done" ? latest.summary?.coverage_status || "" : "";
  if (isInactiveError(latest) || (coverage && coverage !== "Active")) {
    return { kind: "inactive", label: `Inactive · ${date}`, date };
  }
  // only an explicit Active establishes coverage
  if (coverage !== "Active") return { kind: "unverified", label: `Coverage status not reported · ${date}`, date };
  const ageDays = (new Date(now).getTime() - new Date(when).getTime()) / (24 * 60 * 60 * 1000);
  if (ageDays > (staleDays || DEFAULT_STALE_DAYS)) return { kind: "stale", label: `Stale · verified ${date}`, date };
  return { kind: "active", label: `Verified Active · ${date}`, date };
};

/** The API's refusal codes for a breakdown (409) in plain language. */
export const BREAKDOWN_REFUSALS = {
  payer_has_no_portal_breakdown: "This insurer has no portal support",
  no_portal_login: "Connect the insurer's portal first",
  clinic_not_set_up: "Finish the insurance verification setup first",
  already_full_detail: "The last check already returned the full breakdown",
};

/**
 * What the card offers for this insurer (2026-10-21 labelling):
 * - direct: "Check eligibility & benefits" — the breakdown comes back with the check;
 * - direct_and_portal / portal_only: "Check eligibility" plus "Full breakdown", disabled
 *   with its reason until the portal is connected; a portal-only insurer with no login
 *   also links to the portal step of the setup;
 * - no portal support: the breakdown is disabled with that reason;
 * - the last check for this insurer already returned full detail: no breakdown button.
 *
 * @param payer { commonPayerId, name, access } or null (insurer unknown)
 * @param portalState 'connected' | 'needs_attention' | 'not_connected' | undefined (not readable)
 * @param offer the prefill's `breakdown` ({ offered, reason, portal_state }): the API's own
 *   decision, readable by every staff member; when present it decides over `portalState`
 */
export const checkActions = ({ payer, portalState, offer, verifications, credits = DEFAULT_CREDITS }) => {
  const access = payer?.access || "unknown";
  const name = payer?.name || "insurer";
  const eligibilityCost = credits?.eligibility ?? DEFAULT_CREDITS.eligibility;
  const breakdownCost = credits?.breakdown ?? DEFAULT_CREDITS.breakdown;
  const check = {
    label: `${access === "direct" ? "Check eligibility & benefits" : "Check eligibility"} · ${eligibilityCost} credits`,
    cost: eligibilityCost,
  };
  const lastForPayer = doneResults(verifications)
    .filter((v) => !payer?.commonPayerId || v.payer?.common_payer_id === payer.commonPayerId).sort(byNewest)[0];
  if (lastForPayer?.full_detail) return { check, breakdown: null, connect: null };

  const breakdownLabel = `Full breakdown · ${breakdownCost} credits · usually ready within a few hours`;
  const noPortal = {
    check,
    breakdown: { label: `Full breakdown · ${breakdownCost} credits`, cost: breakdownCost, disabled: true, reason: "This insurer has no portal support" },
    connect: null,
  };
  const connectLink = (state) => (state !== "connected" && access === "portal_only"
    ? { label: `Connect the ${name} portal`, href: `${SETUP_PATH}?step=portals` }
    : null);

  if (offer && typeof offer === "object") {
    if (offer.reason === "payer_has_no_portal_breakdown") return noPortal;
    // the API checks full detail last: "already_full_detail" means everything else held, and whether
    // the latest result still has full detail is read from the results above (fresh after every check)
    const offered = !!offer.offered || offer.reason === "already_full_detail";
    const state = offer.portal_state || (offered ? "connected" : "not_connected");
    const ready = offered && state === "connected";
    return {
      check,
      breakdown: {
        label: breakdownLabel,
        cost: breakdownCost,
        disabled: !ready,
        reason: ready ? "" : state === "connected" && offer.reason ? BREAKDOWN_REFUSALS[offer.reason] || "A full breakdown is not available"
          : `Connect the ${name} portal first`,
      },
      connect: connectLink(state),
    };
  }

  const hasPortal = access === "portal_only" || access === "direct_and_portal";
  if (!hasPortal) return noPortal;
  // portal state unknown (an API without the prefill's offer, and the user cannot read the portals): the API decides on submit
  const connected = portalState === undefined || portalState === "connected";
  const breakdown = {
    label: breakdownLabel,
    cost: breakdownCost,
    disabled: !connected,
    reason: connected ? "" : `Connect the ${name} portal first`,
  };
  return { check, breakdown, connect: connected ? null : connectLink(portalState) };
};


/**
 * A finished check's outcome in plain language (Zuub's problem titles are a machine
 * catalogue). charged: whether the practice was charged for it.
 */
export const errorMessage = (v) => {
  if (!v || v.status !== "failed") return null;
  const type = String(v.error?.type || "");
  const status = Number(v.error?.status) || 0;
  const charged = !!v.credits?.charged;
  if (/inactive/i.test(type)) {
    return { kind: "inactive", title: "Coverage is not active", text: "The insurer says this plan is not active. Ask the patient for their current insurance card.", charged, action: "edit" };
  }
  if (/portal.?credentials|credentials.?invalid/i.test(type)) {
    return { kind: "portal_login", title: "The portal login stopped working", text: "Update the insurer's portal login in Settings, then run the breakdown again.", charged, action: "settings" };
  }
  if (/not.?found|no.?match|unknown.?(patient|member|subscriber)/i.test(type) && /patient|member|subscriber|dependent|plan/i.test(type)) {
    return { kind: "not_found", title: "Patient not found", text: "The insurer did not find this patient. Check the member ID and date of birth, edit them and try again.", charged, action: "edit" };
  }
  if (!status || status >= 500 || /maintenance|unavailable|unreachable|timeout/i.test(type)) {
    return { kind: "unavailable", title: "The insurer is unavailable", text: "The insurer's system did not answer. You were not charged; try again in a little while.", charged, action: "retry" };
  }
  return { kind: "other", title: "The check did not complete", text: type ? `The insurer answered: ${type}.` : "The check did not complete.", charged, action: "edit" };
};

export const MISMATCH_FIELDS = {
  first_name: "first name",
  last_name: "last name",
  date_of_birth: "date of birth",
  member_id: "member ID",
  group: "group number",
};

const val = (v) => {
  if (v === undefined || v === null) return undefined;
  if (typeof v !== "object" || Array.isArray(v)) return v;
  if (!("final" in v || "original" in v || "missing" in v || "transformed" in v)) return v;
  if (v.missing === true) return undefined;
  if (v.transformed === false) return Array.isArray(v.original) && v.original.length === 1 ? v.original[0] : v.original;
  return v.final;
};

const shownValue = (field, value) => {
  if (value === undefined || value === null || value === "" || /^redacted$/i.test(String(value))) return "";
  return field === "date_of_birth" ? formatDay(value) : String(value);
};

/**
 * Mismatch warnings: "Insurer has date of birth Jan 2, 1990, chart has Jan 1, 1990" when the
 * stored request and raw result carry both values, else "The insurer's date of birth differs from the chart".
 */
export const mismatchWarnings = (v) => (v?.summary?.mismatches || []).map((field) => {
  const label = MISMATCH_FIELDS[field] || humanize(field);
  const sent = v.request?.dependent || v.request?.subscriber || {};
  const got = v.raw_result?.subscriber || {};
  const pairs = {
    first_name: [val(got.firstName), sent.firstName],
    last_name: [val(got.lastName), sent.lastName],
    date_of_birth: [val(got.dateOfBirth), sent.dateOfBirth],
    member_id: [val(got.id), v.request?.subscriber?.id],
    group: [val(v.raw_result?.plan?.group?.id), v.request?.subscriber?.groupId],
  }[field] || [];
  const insurer = shownValue(field, pairs[0]);
  const chart = shownValue(field, pairs[1]);
  return insurer && chart
    ? `Insurer has ${label} ${insurer}, chart has ${chart}`
    : `The insurer's ${label} differs from the chart`;
});

// ── a running check, followed across the Inbox ───────────────────────────────

// A breakdown is ready hours later, usually while the office is on another conversation:
// the card records each running check of the practice here, and the app-level notifier
// (InsuranceVerificationNotifier) follows them until they finish, whatever is on screen.
// One storage entry per check (tabs never overwrite each other's), and once a check is
// announced a separate marker that registrations never write, so neither a late "still
// running" answer nor another tab's stale write can follow it again. Both are dropped a
// week after they were recorded.
const WATCH_PREFIX = (bid) => `iv-watch-${bid}-`;
const ANNOUNCED_PREFIX = (bid) => `iv-announced-${bid}-`;
export const WATCH_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;
export const IV_FINISHED_EVENT = "iv:verification-finished";

const readEntry = (key) => {
  try {
    const e = JSON.parse(localStorage.getItem(key) || "null");
    return e && typeof e === "object" && e.id ? e : null;
  } catch (e) {
    return null;
  }
};

const storageKeys = (prefix) => {
  const keys = [];
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith(prefix)) keys.push(k);
    }
  } catch (e) {
    // storage unavailable: nothing is followed
  }
  return keys;
};

const removeKey = (key) => {
  try {
    localStorage.removeItem(key);
    return true;
  } catch (e) {
    return false;
  }
};

const setKey = (key, value) => {
  try {
    localStorage.setItem(key, value);
    return true;
  } catch (e) {
    // storage full or unavailable: the open card still follows its own check
    return false;
  }
};

const announcedAt = (bid, id) => {
  try {
    return localStorage.getItem(`${ANNOUNCED_PREFIX(bid)}${id}`);
  } catch (e) {
    return null;
  }
};

// what storage could not take is still followed by this tab (until it reloads)
const unstoredEntries = new Map();
const unstoredAnnounced = new Set();
export const forgetUnstoredWatches = () => {
  unstoredEntries.clear();
  unstoredAnnounced.clear();
};
const isAnnounced = (bid, id) => announcedAt(bid, id) !== null || unstoredAnnounced.has(`${ANNOUNCED_PREFIX(bid)}${id}`);

/** The practice's running checks still to announce, oldest first (expired and announced ones are removed). */
export const watchedVerifications = (bid, { now = Date.now() } = {}) => {
  if (!bid) return [];
  for (const key of storageKeys(ANNOUNCED_PREFIX(bid))) {
    if (now - Number(localStorage.getItem(key) || 0) > WATCH_MAX_AGE_MS) removeKey(key);
  }
  const list = [];
  for (const key of storageKeys(WATCH_PREFIX(bid))) {
    const e = readEntry(key);
    if (!e || now - Number(e.since || 0) > WATCH_MAX_AGE_MS || isAnnounced(bid, e.id)) {
      removeKey(key);
      continue;
    }
    list.push(e);
  }
  for (const [key, e] of unstoredEntries) {
    if (!key.startsWith(WATCH_PREFIX(bid))) continue;
    if (now - Number(e.since || 0) > WATCH_MAX_AGE_MS || isAnnounced(bid, e.id)) unstoredEntries.delete(key);
    else if (!list.some((x) => x.id === e.id)) list.push(e);
  }
  return list.sort((a, b) => Number(a.since || 0) - Number(b.since || 0));
};

/**
 * Follow a running check (entry: { id, type, patientName, payerName, status }) until it
 * finishes. A check already announced is never followed again.
 */
export const watchVerification = (bid, entry, { now = Date.now() } = {}) => {
  if (!bid || !entry?.id || isAnnounced(bid, entry.id)) return;
  const key = `${WATCH_PREFIX(bid)}${entry.id}`;
  const before = readEntry(key) || unstoredEntries.get(key);
  const next = before ? { ...before, ...entry, since: before.since } : { ...entry, since: now };
  if (setKey(key, JSON.stringify(next))) unstoredEntries.delete(key);
  else unstoredEntries.set(key, next);
};

/**
 * Mark a check announced. Returns false when it was not followed or already announced
 * (by another tab or poller), so it is announced once.
 */
export const finishVerification = (bid, id, { now = Date.now() } = {}) => {
  if (!bid || !id) return false;
  const key = `${WATCH_PREFIX(bid)}${id}`;
  if (!(readEntry(key) || unstoredEntries.has(key)) || isAnnounced(bid, id)) return false;
  const marker = `${ANNOUNCED_PREFIX(bid)}${id}`;
  // the marker is a few bytes; storage too full even for it: the entry makes room, else this tab remembers it
  const marked = setKey(marker, String(now)) || (removeKey(key) && setKey(marker, String(now)));
  if (!marked) unstoredAnnounced.add(marker);
  removeKey(key);
  unstoredEntries.delete(key);
  return true;
};

/** Every check still running (requested, in progress or retrying), newest first. */
export const pendingVerifications = (verifications) => (verifications || [])
  .filter((v) => v && ["requested", "in_progress", "retrying"].includes(v.status)).sort(byNewest);

/**
 * The Inbox notification for a finished check. The insurer is named as the card that followed it
 * named it (the patient's own insurer: SS-4455), else as the API stored it.
 */
export const finishedNotice = (v, entry = {}) => {
  const done = v?.status === "done";
  const kind = (v?.type || entry.type) === "breakdown" ? "Full breakdown" : "Eligibility check";
  return {
    title: done ? `${kind} ready` : `${kind} did not complete`,
    description: [entry.patientName, entry.payerName || v?.payer?.name].filter(Boolean).join(" · "),
    status: done ? "success" : "warning",
  };
};

/** In-progress breakdown copy. */
export const pendingLabel = (v) => {
  if (!v) return "";
  if (v.status === "retrying") return "Retrying: the insurer's site is down";
  return "Requested · we'll let you know";
};

const has = (x) => x !== undefined && x !== null;

// one benefit: the same network (status and name), coverage level and benefit categories
const sameBenefit = (a, b) => a.network?.status === b.network?.status && (a.network?.name || "") === (b.network?.name || "")
  && a.coverage_level === b.coverage_level && [...(a.categories || [])].sort().join(",") === [...(b.categories || [])].sort().join(",");

/**
 * The annual maximum to show: in-network first. A benefit-period total (not Lifetime) takes
 * its remaining amount from the payer's separate "Remaining" record of the SAME benefit when
 * it carries none; a remaining-only record stands alone. Within a network, a record with a
 * total comes first.
 */
export const annualMaximum = (maximums) => {
  const list = (maximums || []).filter((m) => m && m.period !== "Lifetime");
  // a record with what is left but no total (the payer's "Remaining" period, or a period with no total)
  const remainingOnly = list.filter((m) => !has(m.amount) && has(m.remaining));
  const used = new Set();
  const totals = list.filter((m) => has(m.amount)).map((t) => {
    if (has(t.remaining)) return t;
    // the payer's own "Remaining" record of this benefit, or one of the same period
    const r = remainingOnly.find((x) => !used.has(x) && sameBenefit(x, t) && (x.period === "Remaining" || x.period === t.period));
    if (!r) return t;
    used.add(r);
    return { ...t, remaining: r.remaining };
  });
  const candidates = [...totals, ...remainingOnly.filter((r) => !used.has(r))];
  return candidates.sort((a, b) => Number(b.network?.status === "InNetwork") - Number(a.network?.status === "InNetwork")
    || Number(has(b.amount)) - Number(has(a.amount)))[0] || null;
};

/**
 * "$933.30 of $2,000 left", "$933.30 left" (no total known) or "$2,000" (no remaining known),
 * marked when the figure is the out-of-network one.
 */
export const amountLine = (a) => {
  if (!a) return "";
  const text = has(a.amount) && has(a.remaining) ? `${money(a.remaining)} of ${money(a.amount)} left`
    : has(a.remaining) ? `${money(a.remaining)} left` : money(a.amount);
  return a.network?.status === "OutOfNetwork" ? `${text} · out-of-network` : text;
};

/** The PMS write state of a result, or null when the practice's PMS takes no files. */
export const pmsState = (v) => {
  const s = v?.pms_write?.state;
  if (s === "written") return { kind: "written", label: "Saved to PMS ✓" };
  if (s === "failed") return { kind: "failed", label: "Couldn't save to PMS" };
  if (s === "pending" && v.status === "done") return { kind: "pending", label: "Saving to PMS…" };
  return null;
};

/**
 * Once a check is done the API answers the practice's questions and saves the PDF to the PMS in
 * the background (it waits up to 2 minutes for the answers and holds a PMS save for 5): a result
 * still doing either is settling. It is followed for at most SETTLE_WINDOW_MS; one still pending
 * past that is stuck. `windowMs` bounds how long ago (by the API's clock, `completed`) it finished.
 */
export const SETTLE_WINDOW_MS = 10 * 60 * 1000;
// a workstation clock may be off from the API's: the card only skips results older than this
export const SETTLE_RECENT_MS = 24 * 60 * 60 * 1000;
export const settlingAfterDone = (v, { now = Date.now(), windowMs = SETTLE_WINDOW_MS } = {}) => {
  if (!v || v.status !== "done") return false;
  if (v.answers?.status !== "pending" && v.pms_write?.state !== "pending") return false;
  const finished = new Date(v.completed || v.created || NaN).getTime();
  return Number.isFinite(finished) && Math.abs(now - finished) < windowMs;
};

/** Whether the questions block leads: the practice has questions on and answers exist. */
export const answersFirst = (v, settings) => {
  const enabled = (settings?.questions || []).filter((q) => q && q.enabled !== false);
  return !!enabled.length && (settings?.results_view || "answers") === "answers"
    && !!v?.answers && v.answers.status !== "none";
};

// ── the procedure table (raw result) ─────────────────────────────────────────

const procedurePercent = (p) => {
  for (const c of p?.coinsurance || []) {
    const x = val(c?.percentage);
    if (typeof x === "number") return { start: x, end: x };
    if (x && typeof x === "object" && (x.start !== undefined || x.end !== undefined)) return { start: x.start ?? x.end, end: x.end ?? x.start };
    const n = Number(x);
    if (x !== undefined && x !== null && x !== "" && Number.isFinite(n)) return { start: n, end: n };
  }
  return null;
};

/**
 * One row per procedure code and network from a raw result: coverage %, frequency,
 * age limit, pre-authorization and the last service date.
 */
export const procedureRows = (raw) => {
  const lastDates = {};
  for (const h of raw?.subscriber?.history || []) {
    const d = val(h?.date);
    if (!d) continue;
    for (const code of h.codes || []) {
      if (!lastDates[code] || new Date(lastDates[code]) < new Date(d)) lastDates[code] = d;
    }
  }
  const rows = [];
  const seen = new Set();
  for (const p of raw?.plan?.procedures || []) {
    if (!p?.code) continue;
    const net = networkLabel(p.network);
    const key = `${p.code}|${net}`;
    if (seen.has(key)) continue;
    seen.add(key);
    const limits = Array.isArray(val(p.limitations)) ? val(p.limitations) : [];
    const freq = limits.find((l) => l?.frequency)?.frequency;
    const age = limits.find((l) => l?.age)?.age;
    const pct = procedurePercent(p);
    const pre = val(p.preAuthorization);
    rows.push({
      code: p.code,
      category: humanize(p.category),
      network: net,
      coverage: pct ? percentLabel(pct.start, pct.end) : "",
      frequency: freq ? `${freq.count?.value ?? "?"} per ${[freq.period?.value, humanize(freq.period?.unit)].filter((x) => x !== undefined && x !== "").join(" ")}` : "",
      age: age ? `${age.start ?? 0}–${age.end ?? "any"}` : "",
      preauth: pre === true ? "Required" : pre === false ? "No" : "",
      last: lastDates[p.code] ? formatDay(lastDates[p.code]) : "",
    });
  }
  return rows;
};

export const filterProcedures = (rows, search) => {
  const q = String(search || "").trim().toLowerCase();
  if (!q) return rows;
  return rows.filter((r) => [r.code, r.category, r.network].some((x) => String(x || "").toLowerCase().includes(q)));
};

// ── settings ─────────────────────────────────────────────────────────────────

export const EXAMPLE_QUESTIONS = [
  "What is the frequency for D1110?",
  "Is there a missing-tooth clause?",
  "What is the ortho lifetime maximum?",
  "Are there waiting periods for major services?",
];

/** The settings' questions before a save: null when valid, else the message to show. */
export const validateQuestions = (questions) => {
  const list = questions || [];
  if (list.length > MAX_QUESTIONS) return `You can have at most ${MAX_QUESTIONS} questions.`;
  for (const q of list) {
    const text = String(q?.text || "").trim();
    if (!text) return "A question can't be empty.";
    if (text.length > MAX_QUESTION_CHARS) return `A question can be at most ${MAX_QUESTION_CHARS} characters.`;
  }
  return null;
};

export const ACCESS_BADGES = {
  direct: { label: "Full breakdown automatic", color: "green" },
  portal_only: { label: "Needs your portal login for the full breakdown", color: "orange" },
  direct_and_portal: { label: "Login optional", color: "blue" },
  none: { label: "Basic eligibility only", color: "gray" },
};

export const PORTAL_STATES = {
  connected: { label: "Connected", color: "green" },
  needs_attention: { label: "Needs attention", color: "orange" },
  not_connected: { label: "Not connected", color: "gray" },
};

/** The guided setup's summary sentence from setup/payers' readiness shares. */
export const readinessSentence = (readiness) => {
  const pct = (x) => `${Math.round(Number(x?.patient_share) || 0)}%`;
  return `Full breakdown for ${pct(readiness?.full_automatic)} of your patients automatically, `
    + `${pct(readiness?.full_with_login)} after the logins you skipped, `
    + `${pct(readiness?.basic_only)} basic eligibility only`;
};

// ── worklist ─────────────────────────────────────────────────────────────────

export const WORKLIST_STATES = {
  verified: { label: "Verified", color: "green" },
  stale: { label: "Stale", color: "orange" },
  inactive: { label: "Inactive", color: "red" },
  never: { label: "Never verified", color: "gray" },
};

/** The rows "Check all unverified" runs: one per patient never verified or stale. */
export const unverifiedPatients = (rows) => {
  const seen = new Set();
  return (rows || []).filter((r) => {
    if (!r?.patient_id || !["never", "stale"].includes(r.state) || seen.has(r.patient_id)) return false;
    seen.add(r.patient_id);
    return true;
  });
};

/**
 * What a check run from the worklist came back with: the coverage status, or a failure in the
 * Insurance card's plain language with whether it was charged (never Zuub's problem title).
 */
export const checkOutcome = (v) => {
  if (v?.status === "done") return v.summary?.coverage_status || "Done";
  if (v?.status === "failed") {
    const failure = errorMessage(v);
    return `${failure.title} · ${failure.charged ? "Charged" : "Not charged"}`;
  }
  return "Requested";
};

/** A check body from the prefill (the same fields the confirm sheet sends). */
export const checkBodyFromPrefill = (p, type = "eligibility") => {
  const sub = p?.subscriber || {};
  const dep = p?.dependent;
  return {
    type,
    subscriber: {
      firstName: sub.firstName || "", lastName: sub.lastName || "", dateOfBirth: sub.dateOfBirth || "",
      id: sub.id || p?.member_id || "", groupId: sub.groupId || p?.group_number || "",
      ...(sub.zipCode ? { zipCode: sub.zipCode } : {}),
    },
    ...(dep ? { dependent: { firstName: dep.firstName || "", lastName: dep.lastName || "", dateOfBirth: dep.dateOfBirth || "" } } : {}),
    payerId: p?.payerId || "",
    providerId: p?.providerId || "",
    serviceDate: p?.serviceDate || "",
  };
};
